import { z } from "zod";
import { capabilitySchema } from "./capabilities";
import { studyMemberRoleSchema as StudyMemberRoleSchema } from "./studyAccess";
import { LanguageCodeSchema, TranslationMapSchema } from "./utils";
import {
  ScheduleInputSchema,
  ScheduleSchema,
  ScheduledSurveyStateSchema,
  SurveyOccurrenceSchema,
  TriggerSurveyInputSchema,
} from "./surveySchedule";

/**
 * Mirrors the Prisma `StudyStatus` enum; clients cannot import the generated
 * one. One way only: DRAFT -> ACTIVE -> CLOSED.
 *
 * DRAFT is editable and invisible to patients, ACTIVE is frozen and collecting,
 * CLOSED is frozen and read-only. There is no unpublish and no reopen.
 */
export const StudyStatusSchema = z.enum(["DRAFT", "ACTIVE", "CLOSED"]);
export type StudyStatus = z.infer<typeof StudyStatusSchema>;

/**
 * Who can SEE a study, independently of who may act in it.
 *
 * PRIVATE is membership: its creator, an admin, and whoever was invited.
 * PUBLIC opens the read to every authenticated staff account and grants
 * nothing else — acting still needs membership, so a public study is
 * view-only to everyone who was not invited. Changing it is the PI's or an
 * admin's call, on the access route rather than the study edit, because it
 * is a decision about the audience and not about the protocol (and it stays
 * available once the study is frozen).
 */
export const StudyVisibilitySchema = z.enum(["PRIVATE", "PUBLIC"]);
export type StudyVisibility = z.infer<typeof StudyVisibilitySchema>;

const CreatorInfoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

/**
 * One scheduled appearance of a survey in a study.
 *
 * A study may hold the same survey several times on different schedules, which
 * is what a real protocol looks like: the same questionnaire three weeks before
 * surgery, on the day, and weekly for four weeks after. That is why this is not
 * keyed by `surveyId` and why it carries its own key and label. Without them a
 * patient would see the same title three times in a list with no way to tell
 * which is which, and an export would have three identically named columns.
 */
/**
 * The questionnaire a schedule collects, as every list of schedules carries it.
 *
 * Shared rather than inlined twice because a study's schedules and a patient's
 * own now render the same way — grouped UNDER their questionnaire, which is
 * what the storage has looked like since a plan became (owner, survey).
 */
export const ScheduledSurveySummarySchema = z.object({
  id: z.string(),
  title: TranslationMapSchema,
  description: TranslationMapSchema,
  /**
   * The survey's own declared languages, carried here so a client can
   * compare them against the study's without a second request. Both sides
   * declare their languages in a column, so this is a comparison of two
   * lists.
   */
  languages: z.array(LanguageCodeSchema),
  /**
   * Which scoring plugin reads this questionnaire, or null for one that is
   * collected and not scored. Carried so a client knows whether an analysis
   * link belongs beside it — the alternative is offering the link on every
   * questionnaire and landing half of them on an empty page.
   */
  analysisPluginId: z.string().nullable(),
});
export type ScheduledSurveySummary = z.infer<
  typeof ScheduledSurveySummarySchema
>;

export const ScheduledSurveyResponseSchema = z.object({
  id: z.string(),
  /** Stable slug, unique within the study ("baseline", "d0", "post-op-w1").
   *  Doubles as the export column identifier. */
  key: z.string(),
  label: TranslationMapSchema,
  survey: ScheduledSurveySummarySchema,
  schedule: ScheduleSchema,
});
export type ScheduledSurveyResponse = z.infer<
  typeof ScheduledSurveyResponseSchema
>;

export const PatientStudyEnrollmentSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  consentGiven: z.boolean(),
  consentSignedAt: z.coerce.date().nullable(),
  enrolledAt: z.coerce.date(),
});
export type PatientStudyEnrollment = z.infer<
  typeof PatientStudyEnrollmentSchema
>;

export const StudyResponseSchema = z.object({
  id: z.string(),
  title: TranslationMapSchema,
  description: TranslationMapSchema.nullable(),
  consentForm: TranslationMapSchema,
  /**
   * Declared, not derived: a study may list a language whose text is not
   * written yet. Mirrors `Survey.languages`, which is what makes "does this
   * survey cover this study?" a comparison of two lists.
   */
  languages: z.array(LanguageCodeSchema),
  status: StudyStatusSchema,
  visibility: StudyVisibilitySchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /** Set on DRAFT -> ACTIVE and never cleared. Null while the study is a draft. */
  activatedAt: z.coerce.date().nullable(),
  /** Set on ACTIVE -> CLOSED and never cleared. */
  closedAt: z.coerce.date().nullable(),
  /** WHO OPENED IT, a historical fact. Authority lives in StudyMember rows:
   *  read `viewerRole` for what the caller is here. */
  createdBy: CreatorInfoSchema,
  /**
   * The caller's own role in this study, or null when they are not a member
   * (an admin reading any study, or anyone reading a public one). Server-
   * computed, like `permissions`: stewardship is not something a client
   * should infer from an id comparison.
   *
   * Optional for the same reason `permissions` is — the MOBILE study reads
   * share this schema, and a patient has no staff role in their own study.
   */
  viewerRole: StudyMemberRoleSchema.nullable().optional(),
  scheduledSurveys: z.array(ScheduledSurveyResponseSchema),
  patientStudies: z.array(PatientStudyEnrollmentSchema),
  /**
   * Server-computed conclusions about what THE CALLER may do to THIS study,
   * grant-aware, so the client gates buttons without evaluating anything.
   * Keyed on the one capability vocabulary (a typo'd key is a compile error,
   * not a silently disabled button) and carrying only what the dashboard
   * actually gates per study. Optional because the mobile study reads share
   * this schema and a patient has no staff permissions to report.
   */
  permissions: z.partialRecord(capabilitySchema, z.boolean()).optional(),
});
export type StudyResponse = z.infer<typeof StudyResponseSchema>;

export const ScheduledSurveyInputSchema = z.object({
  surveyId: z.string(),
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(
      /^[a-z0-9][a-z0-9-]*$/,
      "Expected a lowercase slug, e.g. post-op-w1",
    ),
  label: TranslationMapSchema,
  schedule: ScheduleInputSchema,
});
export type ScheduledSurveyInput = z.infer<typeof ScheduledSurveyInputSchema>;

/**
 * No `status`: a study is always born DRAFT. Activation is a validated
 * transition that stamps `activatedAt`, and accepting a status here would be a
 * supported way to skip both. Same reasoning keeps it out of the update input:
 * `PATCH /studies/:id/status` is the single writer.
 */
export const CreateStudyInputSchema = z.object({
  title: TranslationMapSchema,
  description: TranslationMapSchema.optional(),
  consentForm: TranslationMapSchema,
  /** At least one. May include a language whose text is not written yet. */
  languages: z.array(LanguageCodeSchema).min(1),
  scheduledSurveys: z.array(ScheduledSurveyInputSchema).optional().default([]),
});
export type CreateStudyInput = z.infer<typeof CreateStudyInputSchema>;

export const UpdateStudyInputSchema = z.object({
  title: TranslationMapSchema.optional(),
  description: TranslationMapSchema.optional(),
  consentForm: TranslationMapSchema.optional(),
  languages: z.array(LanguageCodeSchema).min(1).optional(),
});
export type UpdateStudyInput = z.infer<typeof UpdateStudyInputSchema>;

/**
 * One endpoint carrying the TARGET state rather than a verb, matching
 * `PATCH /patients/:id/archive` and `PATCH /users/:id/archive`. The transition
 * table then lives in exactly one place on the server, which is what makes
 * "status has a single writer" checkable.
 */
export const UpdateStudyStatusInputSchema = z.object({
  status: StudyStatusSchema,
});
export type UpdateStudyStatusInput = z.infer<
  typeof UpdateStudyStatusInputSchema
>;

export const AddScheduledSurveyInputSchema = ScheduledSurveyInputSchema;
export type AddScheduledSurveyInput = ScheduledSurveyInput;

/** The survey a schedule points at is fixed once attached: changing it would
 *  reuse a key and a history that belong to a different instrument. */
export const UpdateScheduledSurveyInputSchema = z.object({
  label: TranslationMapSchema.optional(),
  schedule: ScheduleInputSchema.optional(),
});
export type UpdateScheduledSurveyInput = z.infer<
  typeof UpdateScheduledSurveyInputSchema
>;
/**
 * Enrolment is a batch: a clinician builds a cohort from a searchable list and
 * commits the selection in one go, so a per-patient endpoint would mean N
 * round trips and N ways to half-fail.
 */
export const EnrollPatientInputSchema = z.object({
  patientIds: z.array(z.string()).min(1).max(200),
});
export type EnrollPatientInput = z.infer<typeof EnrollPatientInputSchema>;

/**
 * Patients already in the study are skipped rather than refused: a colleague
 * enrolling someone in another tab should not fail the whole batch. The counts
 * are what the caller reports back to the user.
 */
export const EnrollPatientsResultSchema = z.object({
  enrolled: z.number(),
  alreadyEnrolled: z.number(),
});
export type EnrollPatientsResult = z.infer<typeof EnrollPatientsResultSchema>;
export const SignConsentInputSchema = z.object({
  // Base64 data-URL from the signature canvas. Low resolution is fine — the
  // cap keeps the payload well under the server's 1 MiB body limit.
  patientSignature: z.string().min(1).max(500_000),
  /**
   * Which language of the consent form was ON SCREEN when the patient signed.
   *
   * The server snapshots the rendered document either way, in every language it
   * was written in, so this is the one thing it cannot work out for itself: a
   * study written in French and Arabic gives no clue which the reader read.
   * Optional because the dashboard's own signing route has no screen to report
   * from, and because a missing language leaves the record readable rather than
   * refusing a consent over metadata.
   */
  language: LanguageCodeSchema.optional(),
});
export type SignConsentInput = z.infer<typeof SignConsentInputSchema>;
export const GetStudyParamsSchema = z.object({ id: z.string() });
export type GetStudyParams = z.infer<typeof GetStudyParamsSchema>;
export const DeleteStudyParamsSchema = z.object({ id: z.string() });
/** Keyed by the junction row, not the survey: the same survey can be attached
 *  to one study several times on different schedules. */
export const ManageScheduledSurveyParamsSchema = z.object({
  studyId: z.string(),
  scheduledSurveyId: z.string(),
});
export type ManageScheduledSurveyParams = z.infer<
  typeof ManageScheduledSurveyParamsSchema
>;

export const PatientScheduledSurveyParamsSchema = z.object({
  studyId: z.string(),
  patientId: z.string(),
  scheduledSurveyId: z.string(),
});
export type PatientScheduledSurveyParams = z.infer<
  typeof PatientScheduledSurveyParamsSchema
>;

export const EnrollPatientParamsSchema = z.object({
  studyId: z.string(),
  patientId: z.string(),
});
export type EnrollPatientParams = z.infer<typeof EnrollPatientParamsSchema>;
export const SignConsentParamsSchema = z.object({
  studyId: z.string(),
  patientId: z.string(),
});
export type SignConsentParams = z.infer<typeof SignConsentParamsSchema>;
export const GetPatientStudiesParamsSchema = z.object({
  patientId: z.string(),
});
export type GetPatientStudiesParams = z.infer<
  typeof GetPatientStudiesParamsSchema
>;
export const GetStudyPatientsParamsSchema = z.object({ studyId: z.string() });
export type GetStudyPatientsParams = z.infer<
  typeof GetStudyPatientsParamsSchema
>;
export const ListQuerySchema = z.object({
  page: z.coerce.number().int().nonnegative().default(0),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;
/**
 * Study-specific so `ListQuerySchema` stays shared with the two patient-list
 * endpoints. Without the filter the list page mixes drafts and closed studies
 * with the live ones forever.
 */
export const ListStudiesQuerySchema = ListQuerySchema.extend({
  status: StudyStatusSchema.optional(),
});
export type ListStudiesQuery = z.infer<typeof ListStudiesQuerySchema>;
export const ListPatientsQuerySchema = ListQuerySchema.extend({
  consentOnly: z.coerce.boolean().optional(),
});
export type ListPatientsQuery = z.infer<typeof ListPatientsQuerySchema>;

export const createStudySchemas = {
  body: CreateStudyInputSchema,
  response: { 201: StudyResponseSchema },
};
export const listStudiesSchemas = {
  querystring: ListStudiesQuerySchema,
  response: {
    200: z.object({
      studies: z.array(StudyResponseSchema),
      totalPages: z.number(),
      currentPage: z.number(),
      total: z.number(),
    }),
  },
};
export const getStudySchemas = {
  params: GetStudyParamsSchema,
  response: { 200: StudyResponseSchema },
};
export const updateStudySchemas = {
  params: GetStudyParamsSchema,
  body: UpdateStudyInputSchema,
  response: { 200: StudyResponseSchema },
};
export const updateStudyStatusSchemas = {
  params: GetStudyParamsSchema,
  body: UpdateStudyStatusInputSchema,
  response: { 200: StudyResponseSchema },
};
export const deleteStudySchemas = {
  params: DeleteStudyParamsSchema,
  response: { 204: z.void() },
};
export const addScheduledSurveySchemas = {
  params: GetStudyParamsSchema,
  body: AddScheduledSurveyInputSchema,
  response: { 200: StudyResponseSchema },
};
export const removeScheduledSurveySchemas = {
  params: ManageScheduledSurveyParamsSchema,
  response: { 200: StudyResponseSchema },
};
export const updateScheduledSurveySchemas = {
  params: ManageScheduledSurveyParamsSchema,
  body: UpdateScheduledSurveyInputSchema,
  response: { 200: StudyResponseSchema },
};
export const enrollPatientSchemas = {
  params: GetStudyParamsSchema,
  body: EnrollPatientInputSchema,
  response: { 201: EnrollPatientsResultSchema },
};
export const removePatientEnrollmentSchemas = {
  params: EnrollPatientParamsSchema,
  response: { 204: z.void() },
};
export const signConsentSchemas = {
  params: SignConsentParamsSchema,
  body: SignConsentInputSchema,
  response: { 200: z.void() },
};

// Mobile variant: the patient signs their own consent and gets the updated
// enrollment back (the response schema strips the signature — it is stored,
// never echoed).
export const signPatientConsentSchemas = {
  params: SignConsentParamsSchema,
  body: SignConsentInputSchema,
  response: { 200: PatientStudyEnrollmentSchema },
};

/**
 * What a patient reads back after signing: the DOCUMENT THEY SIGNED, never the
 * study's template re-rendered.
 *
 * That distinction is the whole reason this is a route of its own rather than
 * a field on the study read. `StudyResponse.consentForm` on the patient-scoped
 * read is the template with this patient's values substituted AT READ TIME, so
 * a corrected name, a renamed facility or simply a later day would all change
 * it — and `{{today}}` is by definition the render instant. Showing that under
 * a "your signed consent" heading would put a document on screen that nobody
 * ever signed.
 *
 * It carries EVERY language the form was snapshotted in, like the snapshot
 * itself, plus `language`: which translation was actually on screen at signing,
 * when the client reported one. A client shows that one and offers the rest.
 *
 * Deliberately WITHOUT the signature image. It is a base64 data URL up to
 * 500 KB, and the patient drew it: rendering it back proves nothing to them
 * and would put the bytes back on a mobile payload that was emptied of them on
 * 2026-08-11. The clinician-facing record, which does need the mark on it, is
 * the dashboard's PDF.
 */
export const SignedConsentDocumentSchema = z.object({
  studyId: z.string(),
  /** Every language the document was snapshotted in, as signed. */
  html: TranslationMapSchema,
  signedAt: z.coerce.date(),
  /** The translation the patient read, or null when the client reported none
   *  (a clinician recording a paper consent deliberately sends none). */
  language: LanguageCodeSchema.nullable(),
});
export type SignedConsentDocument = z.infer<typeof SignedConsentDocumentSchema>;

export const getSignedConsentDocumentSchemas = {
  params: SignConsentParamsSchema,
  response: { 200: SignedConsentDocumentSchema },
};
/**
 * The enrollment as the patient's app and the clinician's patient page see it:
 * every scheduled survey resolved against this patient's own anchors, so
 * "answerable now", "next on Monday" and "missed" are all server facts. The
 * client never derives them, and never treats local draft progress as
 * completion.
 */
export const PatientStudyEnrollmentWithOccurrencesSchema =
  PatientStudyEnrollmentSchema.extend({
    scheduledSurveys: z.array(ScheduledSurveyStateSchema),
    /**
     * Deliberately WITHOUT the signature and without the signed document.
     *
     * The signature is a base64 data URL up to 500 KB and this list is fetched
     * on every visit to a patient's page and on every launch of the patient's
     * app; the signed snapshot is a whole document beside it. Both had exactly
     * one reader, the dashboard's signature dialog, and that is now a link to
     * `GET /studies/:studyId/patients/:patientId/consent/pdf`, which builds the
     * record server-side. So the bytes never leave the database for a screen
     * again. `consentGiven` and `consentSignedAt` above are what a client needs
     * to know a consent exists and to offer the download.
     */
  });
export type PatientStudyEnrollmentWithOccurrences = z.infer<
  typeof PatientStudyEnrollmentWithOccurrencesSchema
>;

export const getPatientStudiesSchemas = {
  params: GetPatientStudiesParamsSchema,
  querystring: ListPatientsQuerySchema,
  response: {
    200: z.object({
      studies: z.array(
        StudyResponseSchema.extend({
          enrollment: PatientStudyEnrollmentWithOccurrencesSchema,
        }),
      ),
      totalPages: z.number(),
      currentPage: z.number(),
      total: z.number(),
    }),
  },
};
/**
 * Deliberately WITHOUT `patientSignature`: it is up to 500 KB per row, and a
 * study's cohort can be large. The signature travels on the patient's own
 * study list instead, where there are only ever a few rows.
 */
export const getStudyPatientsSchemas = {
  params: GetStudyPatientsParamsSchema,
  querystring: ListPatientsQuerySchema,
  response: {
    200: z.object({
      patients: z.array(
        z.object({
          id: z.string(),
          patientId: z.string(),
          studyId: z.string(),
          consentGiven: z.boolean(),
          consentSignedAt: z.coerce.date().nullable(),
          enrolledAt: z.coerce.date(),
          patient: z.object({
            id: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            phone: z.string(),
            birthdate: z.coerce.date(),
            gender: z.enum(["MALE", "FEMALE", "OTHER"]),
            createdAt: z.coerce.date(),
            lastLoginAt: z.coerce.date().nullable(),
          }),
        }),
      ),
      totalPages: z.number(),
      currentPage: z.number(),
      total: z.number(),
    }),
  },
};

/**
 * A clinician firing a MANUAL schedule for one patient. Requires an ACTIVE
 * study and a signed consent, same as any other collection.
 *
 * For a one-shot schedule each trigger is its own occurrence, so the same
 * survey can be fired after every dialysis session. For a recurring one the
 * first trigger starts the clock and a second is a 409: two triggers would
 * mean two overlapping series with no way to say which an answer belongs to.
 */
export const triggerScheduledSurveySchemas = {
  params: PatientScheduledSurveyParamsSchema,
  body: TriggerSurveyInputSchema,
  response: { 201: SurveyOccurrenceSchema },
};
